import Emitter from 'tiny-emitter';
import adapter from 'webrtc-adapter';

const MEDIA_PERMISSIONS = 'MEDIA_PERMISSIONS';

function setMediaPermissions() {
  localStorage.setItem(MEDIA_PERMISSIONS, 'true');
}

function getMediaPermissions() {
  return localStorage.getItem(MEDIA_PERMISSIONS);
}

export const STREAM_EVENTS = {
  CAMERA_STREAM_CLOSE: 'CAMERA_STREAM_CLOSE',
  MICROPHONE_STREAM_CLOSE: 'MICROPHONE_STREAM_CLOSE',
  SCREEN_STREAM_CLOSE: 'SCREEN_STREAM_CLOSE',
  MEDIA_DEVICE_CHANGE: 'MEDIA_DEVICE_CHANGE',
};

class LocalStreams extends Emitter {
  #cameraStream;
  #microphoneStream;
  #screenStream;

  constructor() {
    super();
    this.#listenDeviceChange();
  }

  #listenDeviceChange() {
    try {
      navigator.mediaDevices.ondevicechange = () => {
        this.emit(STREAM_EVENTS.MEDIA_DEVICE_CHANGE);
      };
    } catch (error) {
      console.log(error);
    }
  }

  async createCameraStream(deviceId) {
    const stream = await createCameraStream(deviceId);
    if (stream) {
      this.#cameraStream = stream;
      const track = stream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          this.emit(STREAM_EVENTS.CAMERA_STREAM_CLOSE);
        };
      }
    }
    return stream;
  }

  getCameraStream() {
    return this.#cameraStream;
  }

  closeCameraStream() {
    if (this.#cameraStream) {
      this.#cameraStream.getVideoTracks().forEach((track) => {
        track.stop();
      });
      this.#cameraStream = null;
    }
  }

  async createMicrophoneStream(deviceId) {
    const stream = await createMicrophoneStream(deviceId);
    if (stream) {
      this.#microphoneStream = stream;
      const track = stream.getAudioTracks()[0];
      if (track) {
        track.onended = () => {
          this.emit(STREAM_EVENTS.MICROPHONE_STREAM_CLOSE);
        };
      }
    }
    return stream;
  }

  getMicrophoneStream() {
    return this.#microphoneStream;
  }

  closeMicrophoneStream() {
    if (this.#microphoneStream) {
      this.#microphoneStream.getAudioTracks().forEach((track) => {
        track.stop();
      });
    }
    this.#microphoneStream = null;
  }

  async createScreenStream() {
    const stream = await createScreenStream();
    if (stream) {
      this.#screenStream = stream;
      const track = stream.getVideoTracks()[0];
      if (track) {
        track.onended = () => {
          this.#screenStream = null;
          this.emit(STREAM_EVENTS.SCREEN_STREAM_CLOSE);
        };
      }
    }
    return stream;
  }

  getScreenStream() {
    return this.#screenStream;
  }

  closeScreenStream() {
    if (this.#screenStream) {
      this.#screenStream.getVideoTracks().forEach((track) => {
        track.stop();
      });
      this.#screenStream = null;
    }
  }
}

export const localStreams = new LocalStreams();

export async function getMediaDevices() {
  const videoDevices = [];
  const audioDevices = [];

  if (!getMediaPermissions() || adapter.browserDetails.browser === 'firefox') {
    setMediaPermissions();
    const stream = await navigator.mediaDevices
      .getUserMedia({
        video: true,
        audio: true,
      })
      .catch(() => null);

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  }

  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => []);
  if (devices.length) {
    devices.forEach((device) => {
      // 去重过滤
      const filterDeviceIdArr = ['communications', 'default'];
      if (device.kind === 'videoinput') {
        if (!filterDeviceIdArr.includes(device.deviceId)) {
          videoDevices.push(device);
        }
      }
      if (device.kind === 'audioinput') {
        if (!filterDeviceIdArr.includes(device.deviceId)) {
          audioDevices.push(device);
        }
      }
    });
  }

  return {
    videoDevices,
    audioDevices,
  };
}

// 获取摄像头
export async function createCameraStream(deviceId) {
  console.warn('createCameraStream', deviceId);
  return navigator.mediaDevices
    .getUserMedia({
      video: {
        deviceId,
        width: 1920,
        height: 1080,
        facingMode: true,
      },
    })
    .catch((e) => {
      console.error(e);
      throw e;
    });
}

// 获取麦克风
export async function createMicrophoneStream(deviceId) {
  return navigator.mediaDevices
    .getUserMedia({
      audio: {
        deviceId,
      },
    })
    .catch((e) => {
      console.error(e);
      throw e;
    });
}

export async function createScreenStream() {
  return navigator.mediaDevices
    .getDisplayMedia({
      video: true,
      audio: false,
    })
    .catch((e) => {
      console.error(e);
      return null;
    });
}

export function stopMediaStream(stream) {
  stream.getTracks().forEach((track) => track.stop());
}

let volumeInter = '';
let initialized = false;
let audioContext;

// 清空释放资源
export const closeAudioVolumeIndicator = () => {
  initialized = false;
  (volumeInter && clearInterval(volumeInter)) || (volumeInter = null);
  audioContext && audioContext.close() && (audioContext = null);
};

// 根据stream获取音频音量
export const openAudioVolumeIndicator = (stream, cb) => {
  if (initialized) {
    return;
  }
  closeAudioVolumeIndicator(volumeInter);
  initialized = true;
  audioContext = new (window.AudioContext || window.webkitAudioContext)();
  // 将麦克风的声音输入这个对象
  let mediaStreamSource = {};
  try {
    mediaStreamSource = audioContext.createMediaStreamSource(stream);
  } catch (e) {
    console.error('Indicate Volume Failed', e);
    return;
  }
  // 创建分析节点
  const analyserNode = audioContext.createAnalyser();
  analyserNode.fftSize = 256; // 每次采256个样本
  // 连接节点
  mediaStreamSource.connect(analyserNode);

  // 获取音量数据
  const dataArray = new Uint8Array(analyserNode.fftSize);

  function getVolume() {
    analyserNode.getByteTimeDomainData(dataArray);
    return Math.abs(Math.max(...dataArray)) / 128 - 1; // 128是时域基准值，采样深度为8位，取绝对值比率，返回值区间为[0, 1)
  }

  // 每隔一段时间获取一次音量
  volumeInter = setInterval(() => {
    cb && cb(getVolume());
  }, 100);
};

function generateMirrorImage(video) {
  let canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  let ctx = canvas.getContext('2d');
  ctx.transform(-1, 0, 0, 1, canvas.width, 0);
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const url = canvas.toDataURL('image/jpeg', 0.3);
  const img = new Image();
  return new Promise((resolve) => {
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx = null;
      canvas.width = 0;
      canvas.height = 0;
      canvas = null;
      resolve(img);
    };
    img.src = url;
  });
}

export async function captureVideoFrame(
  mainVideo,
  sideVideo,
  options = {
    mainVideoMirror: false,
    sideVideoMirror: false,
  },
) {
  let canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 180;
  let ctx = canvas.getContext('2d');
  let mainVideoSource = mainVideo;
  if (options.mainVideoMirror) {
    mainVideoSource = await generateMirrorImage(mainVideo);
  }
  const { videoWidth, videoHeight } = mainVideo;
  if (videoWidth / videoHeight >= 16 / 9) {
    const realHeight = (320 * videoHeight) / videoWidth;
    const posY = (180 - realHeight) / 2;
    ctx.drawImage(mainVideoSource, 0, posY, canvas.width, realHeight);
  } else {
    const realWidth = (180 * videoWidth) / videoHeight;
    const posX = (320 - realWidth) / 2;
    ctx.drawImage(mainVideoSource, posX, 0, realWidth, canvas.height);
  }

  if (sideVideo) {
    let sideVideoSource = sideVideo;
    if (options.sideVideoMirror) {
      sideVideoSource = await generateMirrorImage(sideVideo);
    }
    ctx.arc(260, 120, 40, 0, Math.PI * 2, true); // you can use any shape
    ctx.clip();
    const scaleWidth = Math.round((80 * sideVideo.videoWidth) / sideVideo.videoHeight);
    ctx.drawImage(
      sideVideoSource,
      0,
      0,
      sideVideo.videoWidth,
      sideVideo.videoHeight,
      Math.round(260 - scaleWidth / 2),
      80,
      scaleWidth,
      80,
    );
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx = null;
      canvas = null;
      resolve(blob);
    });
  });
}

export async function captureEditVideoFrame(image, mainVideo, sideVideo) {
  let canvas = document.createElement('canvas');
  const { aspectType } = image;
  let scale = 1;
  let canvasWidth = 320;
  let canvasHeight = 180;
  switch (aspectType) {
    case 0: {
      scale = 320 / 1920;
      break;
    }
    case 1: {
      scale = 320 / 1920;
      canvasWidth = 180;
      canvasHeight = 180;
      break;
    }
    case 2: {
      scale = 180 / 1920;
      canvasWidth = (180 * 1080) / 1920;
      canvasHeight = 180;
      break;
    }
  }

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  let ctx = canvas.getContext('2d');

  if (!image.el.complete) {
    await new Promise((resolve) => (image.el.onload = resolve));
  }
  const imageRealWidth = aspectType === 2 ? (1080 * 1080) / 1920 : aspectType === 1 ? 1080 : 1920;
  const imageX = (1920 - imageRealWidth) / 2;
  ctx.drawImage(image.el, imageX, 0, imageRealWidth, 1080, 0, 0, canvasWidth, canvasHeight);
  ctx.save();

  if (mainVideo.layout?.show) {
    const {
      el,
      layout: {
        geometry: { width, height, positionX, positionY, radius },
      },
      mirror,
    } = mainVideo;
    let source = el;
    if (mirror) {
      source = await generateMirrorImage(el);
    }
    const { videoWidth, videoHeight } = el;
    let realWidth = width;
    let realHeight = height;
    let realX = positionX;
    let realY = positionY;
    if (width / height >= videoWidth / videoHeight) {
      realWidth = (videoWidth / videoHeight) * height;
      realX = positionX + (width - realWidth) / 2;
    } else {
      realHeight = (width * videoHeight) / videoWidth;
      realY = positionY + (height - realHeight) / 2;
    }

    ctx.beginPath();
    ctx.roundRect(realX * scale, realY * scale, realWidth * scale, realHeight * scale, [
      radius * scale,
    ]);
    ctx.clip();
    ctx.drawImage(
      source,
      0,
      0,
      videoWidth,
      videoHeight,
      realX * scale,
      realY * scale,
      realWidth * scale,
      realHeight * scale,
    );
    ctx.restore();
  }

  if (sideVideo.layout?.show) {
    const {
      el,
      layout: {
        geometry: { width, height, positionX, positionY, radius },
      },
      mirror,
    } = sideVideo;
    let source = el;
    if (mirror) {
      source = await generateMirrorImage(el);
    }
    const { videoWidth, videoHeight } = el;
    let realWidth = videoWidth;
    let realHeight = videoHeight;
    let realX = 0;
    let realY = 0;
    if (width / height >= videoWidth / videoHeight) {
      realHeight = (width * videoHeight) / videoWidth;
      realY = ((realHeight - height) / 2 / realHeight) * videoHeight;
      const copyHeight = realHeight;
      realHeight = (height / copyHeight) * videoHeight;
    } else {
      realWidth = (videoWidth / videoHeight) * height;
      realX = ((realWidth - width) / 2 / realWidth) * videoWidth;
      const copyWidth = realWidth;
      realWidth = (width / copyWidth) * videoWidth;
    }

    ctx.beginPath();
    ctx.roundRect(positionX * scale, positionY * scale, width * scale, height * scale, [
      radius * scale,
    ]);
    ctx.clip();
    ctx.drawImage(
      source,
      realX,
      realY,
      realWidth,
      realHeight,
      positionX * scale,
      positionY * scale,
      width * scale,
      height * scale,
    );
    ctx.restore();
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx = null;
      canvas = null;
      resolve(blob);
    });
  });
}

export function captureVideoStream(video) {
  let canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  let ctx = canvas.getContext('2d');
  let rafId;
  function draw() {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    rafId = requestAnimationFrame(draw);
  }
  draw();
  const stream = canvas.captureStream();
  const stop = () => {
    cancelAnimationFrame(rafId);
    rafId = null;
  };
  video.addEventListener('play', draw);
  video.addEventListener('pause', draw);
  video.addEventListener('ended', draw);
  return {
    stream,
    dispose: () => {
      stop();
      video.removeEventListener('play', draw);
      video.removeEventListener('pause', draw);
      video.removeEventListener('ended', draw);
      stream.getTracks().forEach((track) => track.stop());
      ctx = null;
      canvas = null;
    },
  };
}

export class AudioPreloader {
  #preloaded = {};

  constructor() {}

  preload(audioUrl) {
    if (audioUrl in this.#preloaded || !audioUrl) {
      return;
    }
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = audioUrl;
    link.onload = () => {
      link.onload = null;
      link.remove();
      this.#preloaded[audioUrl] = null;
    };
    this.#preloaded[audioUrl] = link;
    document.head.appendChild(link);
  }

  dispose() {
    Object.values(this.#preloaded).forEach((link) => {
      if (link) {
        link.onload = null;
        link.remove();
      }
    });
    this.#preloaded = {};
  }
}
